import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, assetUrl } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import PageHero from "@/components/PageHero";
import { useSeo } from "@/lib/seo";
import {
  MapPin, PlayCircle, Wind, Drop, Newspaper,
  ArrowSquareOut, GlobeHemisphereWest, Sun, ArrowLeft,
} from "@phosphor-icons/react";

const TYPE_LABEL = { country: "Country", state: "State / Region", city: "City" };

function WeatherCard({ weather, placeName }) {
  if (!weather || weather.temp_c === undefined || weather.temp_c === null)
    return null;
  return (
    <div data-testid="place-weather" className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-[#F27A54]">
        <Sun size={18} weight="fill" />
        <p className="overline text-[10px]">Weather now</p>
      </div>
      <div className="mt-2 flex items-end gap-3">
        <span className="font-display text-4xl font-black">{Math.round(weather.temp_c)}°</span>
        <span className="pb-1.5 text-sm font-semibold text-muted-foreground">{weather.description}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
        {weather.humidity != null && <span className="flex items-center gap-1"><Drop size={13} weight="fill" /> {weather.humidity}% humidity</span>}
        {weather.wind_kmh != null && <span className="flex items-center gap-1"><Wind size={13} weight="fill" /> {Math.round(weather.wind_kmh)} km/h</span>}
      </div>
      {weather.forecast?.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-2">
          {weather.forecast.map((d) => (
            <div key={d.date} className="rounded-lg bg-zinc-50 p-2 text-center">
              <p className="text-[10px] font-bold text-muted-foreground">
                {new Date(d.date).toLocaleDateString(undefined, { weekday: "short" })}
              </p>
              <p className="mt-1 text-xs font-black">{d.max_c != null ? Math.round(d.max_c) : "—"}°</p>
              <p className="text-[10px] text-muted-foreground">{d.min_c != null ? Math.round(d.min_c) : "—"}°</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FactsCard({ place }) {
  const f = place.facts || {};
  const rows = [];
  if (place.country && place.type !== "country") rows.push(["Country", place.country]);
  if (f.continent) rows.push(["Continent", f.continent]);
  if (f.admin1) rows.push(["Region", f.admin1]);
  if (f.country_code) rows.push(["ISO code", f.country_code]);
  if (f.population) rows.push(["Population", Number(f.population).toLocaleString()]);
  if (f.elevation != null) rows.push(["Elevation", `${Math.round(f.elevation)} m`]);
  if (f.timezone) rows.push(["Timezone", f.timezone]);
  if (rows.length === 0) return null;
  return (
    <div data-testid="place-facts" className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-[#2D6A4F]">
        <GlobeHemisphereWest size={18} weight="fill" />
        <p className="overline text-[10px]">Key facts</p>
      </div>
      <dl className="mt-3 space-y-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-3 text-sm">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="font-semibold">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function NewsList({ news }) {
  if (!news?.length) return null;
  return (
    <div data-testid="place-news" className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-[#F27A54]">
        <Newspaper size={18} weight="fill" />
        <p className="overline text-[10px]">Latest news</p>
      </div>
      <ul className="mt-3 space-y-3">
        {news.map((n, i) => (
          <li key={i}>
            <a href={n.link} target="_blank" rel="noreferrer" data-testid={`place-news-${i}`}
              className="group block">
              <p className="line-clamp-2 text-sm font-semibold leading-snug group-hover:text-[#F27A54]">{n.title}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {n.source ? `${n.source} · ` : ""}{n.published ? new Date(n.published).toLocaleDateString() : ""}
              </p>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RelatedLinks({ links }) {
  if (!links?.length) return null;
  return (
    <div data-testid="place-related" className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-[#7C3AED]">
        <ArrowSquareOut size={18} weight="fill" />
        <p className="overline text-[10px]">Related on the network</p>
      </div>
      <div className="mt-3 space-y-2">
        {links.map((l) => (
          <a key={l.site} href={l.url} target="_blank" rel="noreferrer" data-testid={`place-link-${l.site}`}
            className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm transition-colors hover:border-[#F27A54] hover:bg-orange-50/50">
            <span>
              <span className="font-bold">{l.label}</span>
              <span className="ml-2 text-[11px] text-muted-foreground">{l.blurb}</span>
            </span>
            <ArrowSquareOut size={14} className="shrink-0 text-muted-foreground" />
          </a>
        ))}
      </div>
    </div>
  );
}

export default function PlacePage() {
  const { type, name } = useParams();
  const decoded = decodeURIComponent(name || "");
  const [data, setData] = useState(undefined);

  useEffect(() => {
    setData(undefined);
    const params = new URLSearchParams({ type, name: decoded });
    axios.get(`${API}/public/place?${params.toString()}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [type, decoded]);

  useSeo({
    title: `${decoded} — PhotoPulse`,
    description: `Photos, weather, news & travel context for ${decoded} on PhotoPulse.pro`,
  });

  if (data === undefined)
    return (
      <PublicLayout>
        <div className="mx-auto max-w-[1200px] px-5 py-8" data-testid="place-loading">
          <PageHero eyebrow={TYPE_LABEL[type] || "Place"} icon={MapPin} title={decoded}
            subtitle="Loading photos, weather & news…" seed={decoded} />
          <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_320px]">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[4/3] animate-pulse rounded-xl bg-zinc-100" />
              ))}
            </div>
            <div className="space-y-5">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-32 animate-pulse rounded-2xl bg-zinc-100" />
              ))}
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  if (data === null)
    return <PublicLayout><div className="mx-auto max-w-2xl px-5 py-20 text-center"><p className="text-muted-foreground">Could not load this place.</p></div></PublicLayout>;

  const { place, count, photos, weather, news, related_links } = data;

  return (
    <PublicLayout>
      <div className="mx-auto max-w-[1200px] px-5 py-8">
        <Link to="/explore" data-testid="place-back" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-600 hover:text-[#F27A54]">
          <ArrowLeft size={16} weight="bold" /> Explore
        </Link>

        <PageHero
          eyebrow={TYPE_LABEL[type] || "Place"}
          icon={MapPin}
          title={place.name}
          image={data.hero_image ? assetUrl(data.hero_image) : undefined}
          subtitle={`${count} photo${count === 1 ? "" : "s"} & video${count === 1 ? "" : "s"} from PhotoPulse${place.country && type !== "country" ? ` · ${place.country}` : ""}`}
          seed={place.name}
        />

        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_320px]">
          {/* Photos */}
          <div>
            <h2 className="font-display text-lg font-black tracking-tight">Photos &amp; videos</h2>
            {photos.length === 0 ? (
              <p className="mt-4 rounded-xl border border-dashed border-border py-10 text-center text-sm text-muted-foreground" data-testid="place-empty">
                No published photos from {place.name} yet.
              </p>
            ) : (
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="place-photos">
                {photos.map((m) => (
                  <Link key={m.id} to={`/p/${m.id}`} data-testid={`place-photo-${m.id}`}
                    className="group overflow-hidden rounded-xl border border-border bg-card transition-transform duration-200 hover:-translate-y-1 hover:shadow-md">
                    <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100">
                      <img src={assetUrl(m.image_url)} alt={m.alt_text || m.title || "photo"} loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      {m.media_type === "video" && <PlayCircle size={38} weight="fill" className="absolute inset-0 m-auto text-white/90 drop-shadow" />}
                    </div>
                    <div className="p-3">
                      <p className="line-clamp-1 text-sm font-bold">{m.title || "Untitled"}</p>
                      {m.location?.city && <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground"><MapPin size={11} weight="fill" /> {m.location.city}</p>}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-5">
            <WeatherCard weather={weather} placeName={place.name} />
            <FactsCard place={place} />
            <NewsList news={news} />
            <RelatedLinks links={related_links} />
          </aside>
        </div>
      </div>
    </PublicLayout>
  );
}
